import { DeleteTravelEsimDialog } from '@/components/DeleteTravelEsimDialog';
import { PermissionGuard } from '@/components/PermissionGuard';
import { StatCard } from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { use404Redirect } from '@/hooks/use404Redirect';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { useOrganizationTravelEsims } from '@/hooks/useTravelEsimOrders';
import { getUserRole } from '@/lib/auth/token';
import { useDebouncedValue } from '@mantine/hooks';
import {
  TRAVEL_ESIM_BENEFICIARY_KINDS,
  TRAVEL_ESIM_QR_CODE_STATES,
  TRAVEL_ESIM_STAY_STATES,
  type OrganizationTravelEsim,
  type TravelEsimBeneficiaryKind,
  type TravelEsimQrCodeState,
  type TravelEsimStayState,
} from '@octave/contracts';
import { Globe, Plane, Plus, QrCode } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type TravelEsimFilters } from './travel-esims/data-table';

const defaultPageInfo = {
  nextPage: null,
  previousPage: null,
  count: 0,
  nbPage: 0,
  page: 1,
};

function isQrCodeState(value: string): value is TravelEsimQrCodeState {
  return (TRAVEL_ESIM_QR_CODE_STATES as readonly string[]).includes(value);
}
function isBeneficiaryKind(value: string): value is TravelEsimBeneficiaryKind {
  return (TRAVEL_ESIM_BENEFICIARY_KINDS as readonly string[]).includes(value);
}
function isStayState(value: string): value is TravelEsimStayState {
  return (TRAVEL_ESIM_STAY_STATES as readonly string[]).includes(value);
}
const nonEmpty = <T,>(values: T[]) => (values.length ? values : undefined);

/** The organization's international eSIMs, on the "Mes lignes" design. */
export function TravelEsimsView() {
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const { data: organization, error: organizationError } =
    useCurrentOrganization();
  use404Redirect(organizationError);

  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo<TravelEsimFilters>(() => {
    return {
      qrCode: nonEmpty(searchParams.getAll('qr').filter(isQrCodeState)),
      beneficiary: nonEmpty(
        searchParams.getAll('beneficiary').filter(isBeneficiaryKind),
      ),
      stay: nonEmpty(searchParams.getAll('stay').filter(isStayState)),
    };
  }, [searchParams]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebouncedValue(searchText, 500);
  const isOctaveAdmin = getUserRole() === 'ADMIN';
  const [esimToDelete, setEsimToDelete] =
    useState<OrganizationTravelEsim | null>(null);

  const { data, isLoading, error, isFetching } = useOrganizationTravelEsims(
    organization?.id,
    {
      page: pagination.page,
      limit: pagination.limit,
      q: debouncedSearchText || undefined,
      qrCode: filters.qrCode,
      beneficiary: filters.beneficiary,
      stay: filters.stay,
    },
  );

  const handleFiltersChange = (next: TravelEsimFilters) => {
    const params = new URLSearchParams(searchParams);
    for (const key of ['qr', 'beneficiary', 'stay']) {
      params.delete(key);
    }
    next.qrCode?.forEach((v) => params.append('qr', v));
    next.beneficiary?.forEach((v) => params.append('beneficiary', v));
    next.stay?.forEach((v) => params.append('stay', v));
    setSearchParams(params, { replace: true });
    setPagination({ page: 1, limit: pagination.limit });
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPagination({ page: 1, limit: pagination.limit });
  };

  const summary = data?.summary;

  return (
    <div className="space-y-8">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              eSIM internationales
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Les eSIM de vos collaborateurs et les forfaits chargés dessus
            </p>
          </div>
          <PermissionGuard permission="write">
            <Button
              size="sm"
              onClick={() =>
                navigate(`/o/${tenantSlug}/catalog?tab=travel-esim`)
              }
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
            >
              <Plus className="h-3.5 w-3.5" />
              Commander une eSIM
            </Button>
          </PermissionGuard>
        </div>

        {summary && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mt-6">
            <StatCard
              icon={Globe}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              label="eSIM commandées"
              value={summary.total}
            />
            <StatCard
              icon={Plane}
              iconBg="bg-sky-50"
              iconColor="text-sky-600"
              label="Séjours en cours"
              value={summary.ongoing}
              onClick={() => handleFiltersChange({ stay: ['ongoing'] })}
            />
            <StatCard
              icon={QrCode}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              label="QR codes en attente"
              value={summary.awaitingQrCode}
              onClick={() => handleFiltersChange({ qrCode: ['pending'] })}
            />
          </div>
        )}

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-4 pb-4">
                <Skeleton className="h-8 w-56" />
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-8 w-32" />
              </div>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">
                Une erreur est survenue lors du chargement des données
              </p>
            </div>
          ) : (
            <DataTable
              data={data?.items ?? []}
              pagination={pagination}
              pageInfo={data?.pageInfo ?? defaultPageInfo}
              onPaginationChange={setPagination}
              onFiltersChange={handleFiltersChange}
              onSearchChange={handleSearchChange}
              currentFilters={filters}
              currentSearchText={searchText}
              isFetching={isFetching && !isLoading}
              onDelete={isOctaveAdmin ? setEsimToDelete : undefined}
            />
          )}
        </div>
      </div>

      <DeleteTravelEsimDialog
        esim={esimToDelete}
        onClose={() => setEsimToDelete(null)}
      />
    </div>
  );
}
